import {
  openFocusAddRecAction,
  openFocusDeleteAction,
  openFocusEditAction,
  openFocusResolveAction,
} from "./focusActions";
import { parseUsd } from "./profitability";
import {
  getScreenerTableSide,
  parseScreenerRowTicker,
} from "./screenerProfitColumn";
import {
  loadOptionsPositionsBySymbol,
  optionsPositionFromRow,
  parseOptionsRowTicker,
} from "./optionsPositionsTable";
import { CUSTOM_PANEL_MARKER_ATTR } from "./tabs";
import { renderRowFilterMenuItem } from "./rowFilterMenuItem";
import {
  isWheelTabTickerFilterActive,
  toggleWheelTabTickerFilter,
  type WheelTickerFilterTarget,
} from "./wheelTickerFilterFromMenu";
import {
  esc,
  normalizeTicker,
  type OptionPosition,
  type WheelRecommendation,
} from "./watchlistData";
import {
  ROBINHOOD_WINDOW_NAME,
  consumeRobinhoodMenuClick,
  openRobinhoodNearbyTab,
  prefetchRobinhoodPage,
  robinhoodHrefForIntent,
  robinhoodIntentFromPosition,
  robinhoodIntentFromRec,
  robinhoodOptionsChainUrl,
  resolveRobinhoodHref,
} from "./robinhoodOpen";
import { openLegendForContext, type LegendOpenContext } from "./legendOpen";
import { isRobinhoodBrokerConnected } from "./robinhoodStatus";

const MENU_ID = "sfe-table-context-menu";

type TableKind = "puts" | "calls" | "options" | "history";

interface MenuItem {
  id: string;
  label: string;
  danger?: boolean;
}

interface RowContext {
  kind: TableKind;
  table: HTMLTableElement;
  row: HTMLTableRowElement;
  ticker: string;
  position: OptionPosition | null;
  rec: WheelRecommendation | null;
}

let menuBound = false;
let positionsBySymbol: Map<string, OptionPosition> = new Map();
let positionsLoadedAt = 0;
let suppressCloseUntil = 0;

function cellNum(row: HTMLTableRowElement, col: string): number | undefined {
  const text = row.querySelector(`td[data-col="${col}"]`)?.textContent;
  const value = parseUsd(text);
  return value ?? undefined;
}

function cellText(row: HTMLTableRowElement, col: string): string {
  return (row.querySelector(`td[data-col="${col}"]`)?.textContent ?? "").trim();
}

export function parseRecommendationFromRow(
  row: HTMLTableRowElement,
  side: "put" | "call",
): WheelRecommendation | null {
  const ticker = normalizeTicker(parseScreenerRowTicker(row));
  const strike = cellNum(row, "strike");
  const expiration =
    row.querySelector("td[data-col='expiration_date']")?.getAttribute("data-expiration-date") ||
    cellText(row, "expiration_date") ||
    cellText(row, "expiration") ||
    cellText(row, "exp_date");
  if (!ticker || strike == null || !expiration) return null;

  const earnings = cellText(row, "next_earnings_date");
  return {
    underlying_ticker: ticker,
    side,
    strike,
    expiration_date: expiration,
    dte: cellNum(row, "dte"),
    mid: cellNum(row, "mid") ?? cellNum(row, "last_price"),
    last_price: cellNum(row, "last_price"),
    roi: cellNum(row, "roi"),
    pop: cellNum(row, "pop"),
    collateral: cellNum(row, "collateral"),
    underlying_price: cellNum(row, "underlying_price") ?? cellNum(row, "price"),
    holding_avg_cost: cellNum(row, "holding_avg_cost"),
    ready_call_contracts: cellNum(row, "ready_call_contracts"),
    available_call_contracts: cellNum(row, "available_call_contracts"),
    opened_calls: cellNum(row, "opened_calls"),
    next_earnings_date: earnings || null,
    expires_after_earnings: row
      .querySelector(`td[data-col="next_earnings_date"]`)
      ?.getAttribute("data-earnings-within-expiry") === "true",
  };
}

function findHistoryTable(): HTMLTableElement | null {
  const byId = document.getElementById("wheelHistoryTable");
  if (byId instanceof HTMLTableElement) return byId;
  const byTxn = document.getElementById("wheelTransactionsTable");
  if (byTxn instanceof HTMLTableElement) return byTxn;

  const panel =
    document.querySelector<HTMLElement>('[data-wheel-view="history"]') ??
    document.querySelector<HTMLElement>('[data-wheel-panel="history"]');
  const table = panel?.querySelector("table");
  return table instanceof HTMLTableElement ? table : null;
}

function parseHistoryRowTicker(row: HTMLTableRowElement, table: HTMLTableElement): string {
  const byCol =
    row.querySelector("td[data-col='ticker'], td[data-col='underlying_ticker']") ??
    row.querySelector("th[data-sort='ticker'], td[data-sort='ticker']");
  if (byCol) {
    const link = byCol.querySelector("a");
    const text = (link?.textContent ?? byCol.textContent ?? "").trim();
    const match = text.match(/^([A-Z]{1,6})\b/);
    if (match) return match[1];
  }

  const headers = Array.from(table.querySelectorAll("thead th"));
  const tickerIdx = headers.findIndex((th) => /ticker/i.test(th.textContent ?? ""));
  if (tickerIdx >= 0) {
    const cells = row.querySelectorAll("td, th");
    const cell = cells[tickerIdx];
    const text = (cell?.textContent ?? "").trim();
    const match = text.match(/^([A-Z]{1,6})\b/);
    if (match) return match[1];
  }
  return "";
}

function resolveTableKind(table: HTMLTableElement): TableKind | null {
  if (table.id === "wheelPutsTable") return "puts";
  if (table.id === "wheelCallsTable") return "calls";
  if (table.id === "wheelPositionsTable") return "options";
  if (table.id === "wheelHistoryTable" || table.id === "wheelTransactionsTable") return "history";
  const history = findHistoryTable();
  if (history && (table === history || history.contains(table))) return "history";
  return null;
}

async function ensurePositionsMap(): Promise<Map<string, OptionPosition>> {
  if (Date.now() - positionsLoadedAt < 30_000 && positionsBySymbol.size) {
    return positionsBySymbol;
  }
  try {
    positionsBySymbol = await loadOptionsPositionsBySymbol(false);
    positionsLoadedAt = Date.now();
  } catch {
    /* keep previous map */
  }
  return positionsBySymbol;
}

async function buildRowContext(
  table: HTMLTableElement,
  row: HTMLTableRowElement,
): Promise<RowContext | null> {
  const kind = resolveTableKind(table);
  if (!kind) return null;

  if (kind === "puts" || kind === "calls") {
    const side = getScreenerTableSide(table);
    const ticker = normalizeTicker(parseScreenerRowTicker(row));
    if (!ticker) return null;
    return {
      kind,
      table,
      row,
      ticker,
      position: null,
      rec: parseRecommendationFromRow(row, side),
    };
  }

  if (kind === "options") {
    const bySymbol = await ensurePositionsMap();
    const ticker = parseOptionsRowTicker(row, table, bySymbol);
    if (!ticker) return null;
    return {
      kind,
      table,
      row,
      ticker,
      position: optionsPositionFromRow(row, bySymbol),
      rec: null,
    };
  }

  const ticker = normalizeTicker(parseHistoryRowTicker(row, table));
  if (!ticker) return null;
  return { kind, table, row, ticker, position: null, rec: null };
}

function wheelTargetForKind(kind: TableKind): WheelTickerFilterTarget {
  if (kind === "puts") return "puts";
  if (kind === "calls") return "calls";
  if (kind === "options") return "options";
  return "history";
}

function parseStocksRowTicker(row: HTMLTableRowElement): string {
  const fromAttr = normalizeTicker(row.getAttribute("data-ticker") ?? "");
  if (fromAttr) return fromAttr;
  const cell = row.querySelector("td[data-col='ticker'], td.tk-cell");
  return normalizeTicker((cell?.textContent ?? "").trim());
}

/** Tables whose row Focus action is the ticker filter (⋮ / right-click). */
function resolveFocusRow(
  table: HTMLTableElement,
  row: HTMLTableRowElement,
): { target: WheelTickerFilterTarget; ticker: string } | null {
  if (table.id === "wheelStockTable") {
    const ticker = parseStocksRowTicker(row);
    return ticker ? { target: "stocks", ticker } : null;
  }
  const kind = resolveTableKind(table);
  if (!kind) return null;
  if (kind === "puts" || kind === "calls") {
    const ticker = normalizeTicker(parseScreenerRowTicker(row));
    return ticker ? { target: kind, ticker } : null;
  }
  if (kind === "options") {
    const ticker = parseOptionsRowTicker(row, table, positionsBySymbol);
    return ticker ? { target: "options", ticker } : null;
  }
  const ticker = normalizeTicker(parseHistoryRowTicker(row, table));
  return ticker ? { target: "history", ticker } : null;
}

function isWebStandaloneHost(): boolean {
  return document.body.classList.contains("sfe-standalone-host");
}

function isInteractiveRowTarget(target: HTMLElement): boolean {
  const interactive = target.closest(
    "button, a, input, select, textarea, label, [role='button'], [role='menuitem'], .recs-ticker",
  );
  if (!interactive || interactive instanceof HTMLTableRowElement) return false;
  return true;
}

async function toggleFocusForRow(
  table: HTMLTableElement,
  row: HTMLTableRowElement,
): Promise<void> {
  if (resolveTableKind(table) === "options") {
    await ensurePositionsMap();
  }
  const resolved = resolveFocusRow(table, row);
  if (!resolved) return;
  toggleWheelTabTickerFilter(resolved.target, resolved.ticker);
}

function robinhoodHrefForContext(ctx: RowContext): string | null {
  if (ctx.kind === "puts" || ctx.kind === "calls") {
    const intent = robinhoodIntentFromRec(ctx.rec, ctx.kind);
    return intent ? robinhoodHrefForIntent(intent) : robinhoodOptionsChainUrl(ctx.ticker);
  }
  if (ctx.kind === "options") {
    const intent = robinhoodIntentFromPosition(ctx.position);
    return intent ? robinhoodHrefForIntent(intent) : robinhoodOptionsChainUrl(ctx.ticker);
  }
  return robinhoodOptionsChainUrl(ctx.ticker);
}

function legendKindForTable(kind: TableKind): LegendOpenContext {
  return kind;
}

function robinhoodIntentForContext(ctx: RowContext) {
  if (ctx.kind === "puts" || ctx.kind === "calls") {
    return robinhoodIntentFromRec(ctx.rec, ctx.kind);
  }
  if (ctx.kind === "options") return robinhoodIntentFromPosition(ctx.position);
  return null;
}

function menuItemsForContext(ctx: RowContext): MenuItem[] {
  const items: MenuItem[] = [{ id: "focus", label: "Focus" }];
  if (isRobinhoodBrokerConnected()) {
    items.push(
      { id: "open-robinhood", label: "Open in Robinhood" },
      { id: "open-legend", label: "Open in Legend" },
    );
  }

  if ((ctx.kind === "puts" || ctx.kind === "calls") && ctx.rec) {
    items.push({ id: "add-ledger", label: "Add" });
  }

  if (ctx.kind === "options" && ctx.position) {
    items.push(
      { id: "edit", label: "Edit" },
      { id: "close", label: "Close" },
      { id: "expired", label: "Expired" },
      { id: "assigned", label: "Assigned" },
      { id: "delete", label: "Delete…", danger: true },
    );
  }

  return items;
}

function closeContextMenu(): void {
  document.getElementById(MENU_ID)?.remove();
}

function positionMenu(menu: HTMLElement, clientX: number, clientY: number): void {
  menu.style.visibility = "hidden";
  menu.style.left = "0";
  menu.style.top = "0";
  document.body.appendChild(menu);
  const pad = 8;
  const viewW = document.documentElement.clientWidth || window.innerWidth;
  const viewH = document.documentElement.clientHeight || window.innerHeight;
  menu.style.maxWidth = `${Math.max(120, viewW - pad * 2)}px`;
  const width = menu.offsetWidth;
  const height = menu.offsetHeight;
  let left = clientX;
  let top = clientY;
  left = Math.min(Math.max(left, pad), Math.max(pad, viewW - width - pad));
  top = Math.min(Math.max(top, pad), Math.max(pad, viewH - height - pad));
  menu.style.left = `${Math.round(left)}px`;
  menu.style.top = `${Math.round(top)}px`;
  menu.style.visibility = "";
}

function openContextMenu(ctx: RowContext, clientX: number, clientY: number): void {
  closeContextMenu();
  const items = menuItemsForContext(ctx);
  if (!items.length) return;

  const menu = document.createElement("div");
  menu.id = MENU_ID;
  menu.className = "sfe-focus-menu-pop sfe-table-context-menu";
  menu.setAttribute("role", "menu");
  menu.setAttribute("data-sfe-injected", "true");
  menu.setAttribute(CUSTOM_PANEL_MARKER_ATTR, "true");
  const filterTarget = wheelTargetForKind(ctx.kind);
  menu.innerHTML = items
    .map((item) => {
      if (item.id === "focus") {
        return renderRowFilterMenuItem({
          active: isWheelTabTickerFilterActive(filterTarget, ctx.ticker),
          actionAttr: "data-ctx-action",
          actionValue: "focus",
        });
      }
      if (item.id === "open-robinhood") {
        const url = robinhoodHrefForContext(ctx);
        return `<a class="sfe-focus-menu-item sfe-focus-menu-item-robinhood" role="menuitem" data-ctx-action="open-robinhood" href="${esc(url ?? "#")}" target="${ROBINHOOD_WINDOW_NAME}" rel="opener">${item.label}</a>`;
      }
      if (item.id === "open-legend") {
        return `<button type="button" class="sfe-focus-menu-item sfe-focus-menu-item-legend" role="menuitem" data-ctx-action="open-legend">${item.label}</button>`;
      }
      return `
      <button type="button"
        class="sfe-focus-menu-item${item.danger ? " sfe-focus-menu-item-danger" : ""}"
        role="menuitem"
        data-ctx-action="${item.id}">
        ${item.label}
      </button>`;
    })
    .join("");

  const robinhoodLink = menu.querySelector<HTMLAnchorElement>("[data-ctx-action='open-robinhood']");
  if (robinhoodLink?.href) prefetchRobinhoodPage(robinhoodLink.href);
  if (robinhoodLink && ctx.kind !== "history") {
    const intent = robinhoodIntentForContext(ctx);
    if (intent && intent.kind !== "stock" && !intent.optionId) {
      void resolveRobinhoodHref(intent).then((resolved) => {
        if (!resolved || !robinhoodLink.isConnected) return;
        robinhoodLink.href = resolved;
        prefetchRobinhoodPage(resolved);
      });
    }
  }

  menu.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("[data-ctx-action]");
    if (!btn) return;
    const action = btn.dataset.ctxAction;
    if (action === "open-robinhood") {
      const href = btn instanceof HTMLAnchorElement ? btn.href : "";
      consumeRobinhoodMenuClick(e, href);
      window.setTimeout(() => closeContextMenu(), 0);
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    closeContextMenu();
    void handleMenuAction(action, ctx);
  });

  suppressCloseUntil = Date.now() + 250;
  positionMenu(menu, clientX, clientY);
}

async function handleMenuAction(action: string | undefined, ctx: RowContext): Promise<void> {
  if (!action) return;

  if (action === "focus") {
    toggleWheelTabTickerFilter(wheelTargetForKind(ctx.kind), ctx.ticker);
    return;
  }

  if (action === "open-robinhood") {
    openRobinhoodNearbyTab(robinhoodHrefForContext(ctx) ?? "");
    return;
  }

  if (action === "open-legend") {
    openLegendForContext(legendKindForTable(ctx.kind), ctx.ticker);
    return;
  }

  if (action === "add-ledger" && ctx.rec) {
    openFocusAddRecAction(ctx.rec);
    return;
  }

  if (!ctx.position) return;
  if (action === "edit") openFocusEditAction(ctx.position);
  else if (action === "close") openFocusResolveAction(ctx.position, "closed");
  else if (action === "expired") openFocusResolveAction(ctx.position, "expired");
  else if (action === "assigned") openFocusResolveAction(ctx.position, "assigned");
  else if (action === "delete") openFocusDeleteAction(ctx.position);
}

function isOurUiTarget(target: HTMLElement): boolean {
  return Boolean(
    target.closest(`#${MENU_ID}`) ||
      target.closest("#sfe-focus-menu-portal") ||
      target.closest("#sfe-focus-action-chip") ||
      target.closest("#sfe-watchlist-zoom-overlay") ||
      target.closest("#sfe-focus-panel") ||
      target.closest("#sfe-custom-panel"),
  );
}

export function tableContextMenuMutation(mutations: MutationRecord[]): boolean {
  for (const mutation of mutations) {
    for (const node of [...mutation.addedNodes, ...mutation.removedNodes]) {
      if (!(node instanceof HTMLElement)) continue;
      if (node.id === MENU_ID || node.closest(`#${MENU_ID}`)) return true;
    }
  }
  return false;
}

export function mountTableContextMenu(): void {
  if (menuBound) return;
  menuBound = true;

  document.addEventListener(
    "contextmenu",
    (e) => {
      const target = e.target as HTMLElement;
      if (isOurUiTarget(target)) return;

      const row = target.closest("tr");
      const table = row?.closest("table");
      if (!(row instanceof HTMLTableRowElement) || !(table instanceof HTMLTableElement)) return;
      if (!resolveTableKind(table)) return;
      if (row.parentElement?.tagName === "THEAD") return;

      e.preventDefault();
      e.stopPropagation();

      void (async () => {
        const ctx = await buildRowContext(table, row);
        if (!ctx) return;
        openContextMenu(ctx, e.clientX, e.clientY);
      })();
    },
    true,
  );

  if (!isWebStandaloneHost()) {
    document.addEventListener(
      "dblclick",
      (e) => {
        const target = e.target as HTMLElement;
        if (isOurUiTarget(target) || isInteractiveRowTarget(target)) return;

        const row = target.closest("tr");
        const table = row?.closest("table");
        if (!(row instanceof HTMLTableRowElement) || !(table instanceof HTMLTableElement)) return;
        if (row.parentElement?.tagName === "THEAD") return;
        if (table.id !== "wheelStockTable" && !resolveTableKind(table)) return;

        e.preventDefault();
        e.stopPropagation();
        closeContextMenu();
        window.getSelection()?.removeAllRanges();
        void toggleFocusForRow(table, row);
      },
      true,
    );
  }

  document.addEventListener(
    "click",
    (e) => {
      if (Date.now() < suppressCloseUntil) return;
      const target = e.target as HTMLElement;
      if (target.closest(`#${MENU_ID}`)) return;
      closeContextMenu();
    },
    true,
  );

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeContextMenu();
  });

  window.addEventListener("scroll", () => closeContextMenu(), true);
  window.addEventListener("resize", () => closeContextMenu());
}
